import { Link } from "@mui/material";

/**
 * Renders an issue message with a link to update permissions if applicable.
 *
 * This utility checks if the issue is about missing facility permissions
 * and renders a link to the account page with omnisearch pre-filled if:
 * - The user has permission to manage users (WriteAllUserTenant)
 * - A tenant ID is available
 *
 * @param issueMessage - The error/issue message to render
 * @param canManageUsers - Whether the current user can manage other users
 * @param tenantId - The current tenant ID (if available)
 * @returns JSX element with the issue message and optional link
 */
export function renderIssueMessage(
  issueMessage: string,
  canManageUsers: boolean,
  tenantId: string | undefined
): React.ReactNode {
  // Check if this is a missing permissions issue
  const missingPermissionsMatch = issueMessage.match(
    /(?:Reviewer|Signer) (.+) must have access to all facilities/
  );

  if (missingPermissionsMatch && canManageUsers && tenantId) {
    const reviewerName = missingPermissionsMatch[1];
    const accountPageUrl = `/o/${tenantId}/account/users?omnisearch=${encodeURIComponent(
      reviewerName
    )}`;

    return (
      <span>
        {`${issueMessage} (please refresh this page after updating the users permissions) `}
        <Link href={accountPageUrl} underline="always" target="_blank">
          Update permissions
        </Link>
      </span>
    );
  }

  return issueMessage;
}
